/**
 * The words and colours every hospital desk uses for the same things.
 *
 * A request is "بانتظار الموافقة المالية" on the Contracts screen and on the lab
 * screen and on the doctor's screen, in the same amber — so a person who works
 * two desks never has to relearn what a chip means. Pure data; nothing here
 * renders.
 */

import { formatMoney as formatLocaleMoney } from '../../domain/locale';
import { printSheet } from '../../services/printSheet';
import { SERVICE_CATEGORY_LABELS } from '../../domain/institutions';

/** The eight departments a patient can be sent to. */
export const DEPARTMENT_META = Object.freeze({
  checkup: { label: 'كشف', icon: 'stethoscope', tone: 'cyan' },
  lab: { label: 'تحاليل', icon: 'flask-conical', tone: 'teal' },
  radiology: { label: 'أشعة', icon: 'scan-line', tone: 'purple' },
  pharmacy: { label: 'صيدلية', icon: 'pill', tone: 'amber' },
  medication: { label: 'أدوية للمقيم', icon: 'syringe', tone: 'orange' },
  icu: { label: 'عناية مركزة', icon: 'heart-pulse', tone: 'red' },
  ward: { label: 'إقامة بالقسم', icon: 'bed-double', tone: 'blue' },
  surgery: { label: 'عملية جراحية', icon: 'scissors', tone: 'rose' },
});

export const DEPARTMENTS = Object.keys(DEPARTMENT_META);

/** A ledger row's or a priced service's category in words: a department, a service category, or a booked consultation. */
export function categoryLabel(key) {
  if (!key) return '—';
  return DEPARTMENT_META[key]?.label || SERVICE_CATEGORY_LABELS[key] || (key === 'consultation' ? 'كشف' : key);
}

export const PRIORITY_META = Object.freeze({
  emergency: { label: 'طارئ — خطر على الحياة', short: 'طارئ', icon: 'siren', className: 'border-red-500/40 bg-red-500/15 text-red-200' },
  normal: { label: 'عادي', short: 'عادي', icon: 'clock', className: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-200' },
  waitlist: { label: 'قائمة الانتظار', short: 'انتظار', icon: 'hourglass', className: 'border-slate-500/30 bg-slate-500/10 text-slate-300' },
});

export const STATUS_META = Object.freeze({
  pending: { label: 'جديد', className: 'border-sky-500/30 bg-sky-500/10 text-sky-200' },
  accepted: { label: 'مقبول', className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' },
  in_progress: { label: 'قيد التنفيذ', className: 'border-violet-500/30 bg-violet-500/10 text-violet-200' },
  completed: { label: 'مكتمل', className: 'border-emerald-500/40 bg-emerald-500/20 text-emerald-100' },
  rejected: { label: 'مرفوض', className: 'border-red-500/30 bg-red-500/10 text-red-200' },
  modification_requested: { label: 'مطلوب تعديل', className: 'border-amber-500/30 bg-amber-500/10 text-amber-200' },
  cancelled: { label: 'ملغي', className: 'border-slate-500/30 bg-slate-500/10 text-slate-400' },
});

export const FINANCE_META = Object.freeze({
  pending: { label: 'بانتظار الموافقة المالية', className: 'border-amber-500/30 bg-amber-500/10 text-amber-200' },
  approved: { label: 'موافقة مالية', className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' },
  rejected: { label: 'رفض مالي', className: 'border-red-500/30 bg-red-500/10 text-red-200' },
});

export const BILLING_META = Object.freeze({
  none: { label: 'لم يُسعَّر', className: 'border-slate-500/30 bg-slate-500/10 text-slate-400' },
  priced: { label: 'مُسعَّر', className: 'border-sky-500/30 bg-sky-500/10 text-sky-200' },
  sent_to_desk: { label: 'مستحق عند الاستقبال', className: 'border-amber-500/30 bg-amber-500/10 text-amber-200' },
  paid: { label: 'تم التحصيل', className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' },
  waived: { label: 'معفى', className: 'border-slate-500/30 bg-slate-500/10 text-slate-300' },
  deferred: { label: 'آجل — بإيصال الاستقبال', className: 'border-amber-500/30 bg-amber-500/10 text-amber-200' },
});

/**
 * How a desk reached the surgeon it recorded a decision for
 * (SURGERY_CONFIRM_ON_BEHALF). Mirrors lib/serviceRouting.js CONFIRMATION_CHANNELS.
 */
export const CONFIRMATION_CHANNELS = Object.freeze({
  phone: { label: 'اتصال هاتفي', icon: 'phone' },
  whatsapp: { label: 'واتساب', icon: 'message-circle' },
  in_person: { label: 'حضورياً', icon: 'user-check' },
  other: { label: 'وسيلة أخرى', icon: 'message-square' },
});

export const ROOM_KIND_META = Object.freeze({
  or: { label: 'غرفة عمليات', icon: 'scissors', tone: 'rose' },
  icu: { label: 'عناية مركزة', icon: 'heart-pulse', tone: 'red' },
  ward: { label: 'قسم داخلي', icon: 'bed-double', tone: 'blue' },
});

export const ASSIGNMENT_META = Object.freeze({
  reserved: { label: 'محجوز — قادم', className: 'bg-amber-500/70 text-white' },
  occupied: { label: 'مشغول', className: 'bg-rose-500/80 text-white' },
  discharged: { label: 'خرج', className: 'bg-slate-600/70 text-slate-100' },
  cancelled: { label: 'ملغي', className: 'bg-slate-700 text-slate-300' },
});

/** "١٬٢٥٠٫٥ ج.م" — the money format every desk prints, in the institution's currency (domain/locale.js). */
export function formatMoney(value) {
  return formatLocaleMoney(Number(value || 0), { digits: 'arab' });
}

export function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('ar-EG', { dateStyle: 'medium' });
}

/** Whole years from a YYYY-MM-DD birth date, or null. */
export function ageFrom(dateOfBirth) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateOfBirth || ''))) return null;
  const born = new Date(`${dateOfBirth}T00:00:00`);
  const now = new Date();
  let age = now.getFullYear() - born.getFullYear();
  if (now.getMonth() < born.getMonth() || (now.getMonth() === born.getMonth() && now.getDate() < born.getDate())) age -= 1;
  return age >= 0 ? age : null;
}

/** Read a picked file as a data URL, the shape every upload in this product takes. */
export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('تعذر قراءة الملف'));
    reader.readAsDataURL(file);
  });
}

/**
 * Print one sheet exactly as it is previewed, fitted to one A4 page
 * (services/printSheet.js). Pass { fit: 'width' } for a document that keeps
 * its own page breaks, such as a day of medical files.
 */
export function printArea(element, options) {
  return printSheet(element, options);
}
